import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { FileText, Loader2, Phone } from "lucide-react";

export const MedicalReportForm = ({
  isRTL,
  formData,
  setFormData,
  doctors,
  submitting,
  handleSubmit,
  onBack,
}) => {
  const seen = new Set();
  const uniqueDoctors = doctors.filter((d) => {
    const key = (d.name || "").trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return (
  <Card className="border-0 shadow-sm" data-testid="medical-report-form-card">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-lg text-sky-700 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {isRTL ? "طلب تقرير طبي" : "Medical Report Request"}
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-500">
          {isRTL ? "← رجوع" : "← Back"}
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Patient name — 3 parts */}
        <div className="space-y-2">
          <Label className={`block ${isRTL ? "text-right" : "text-left"}`}>
            {isRTL ? "الاسم الثلاثي للمريض" : "Patient Full Name (Three parts)"} *
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input
              value={formData.patient_first_name}
              onChange={(e) => setFormData({ ...formData, patient_first_name: e.target.value })}
              placeholder={isRTL ? "الاسم الأول" : "First name"}
              className={isRTL ? "text-right" : "text-left"}
              required
              data-testid="mr-patient-first-name"
            />
            <Input
              value={formData.patient_father_name}
              onChange={(e) => setFormData({ ...formData, patient_father_name: e.target.value })}
              placeholder={isRTL ? "اسم الأب" : "Father's name"}
              className={isRTL ? "text-right" : "text-left"}
              required
              data-testid="mr-patient-father-name"
            />
            <Input
              value={formData.patient_family_name}
              onChange={(e) => setFormData({ ...formData, patient_family_name: e.target.value })}
              placeholder={isRTL ? "اسم العائلة" : "Family name"}
              className={isRTL ? "text-right" : "text-left"}
              required
              data-testid="mr-patient-family-name"
            />
          </div>
        </div>

        {/* Medical file number */}
        <div className="space-y-2">
          <Label className={`block ${isRTL ? "text-right" : "text-left"}`}>
            {isRTL ? "رقم الملف الطبي" : "Medical File Number"} *
          </Label>
          <Input
            value={formData.medical_file_number}
            onChange={(e) => setFormData({ ...formData, medical_file_number: e.target.value })}
            placeholder={isRTL ? "رقم الملف الطبي" : "Medical file number"}
            className={isRTL ? "text-right" : "text-left"}
            required
            data-testid="mr-file-number"
          />
        </div>

        {/* Treating doctor */}
        <div className="space-y-2">
          <Label className={`block ${isRTL ? "text-right" : "text-left"}`}>
            {isRTL ? "الطبيب المعالج" : "Treating Doctor"} *
          </Label>
          <Select
            value={formData.treating_doctor_id}
            onValueChange={(v) => {
              const doc = doctors.find((d) => d.id === v);
              setFormData({
                ...formData,
                treating_doctor_id: v,
                treating_doctor_name: doc?.name || "",
                treating_doctor_specialty: doc?.specialty || "",
              });
            }}
          >
            <SelectTrigger data-testid="mr-doctor-select">
              <SelectValue placeholder={isRTL ? "اختر الطبيب" : "Select doctor"} />
            </SelectTrigger>
            <SelectContent>
              {doctors.length === 0 ? (
                <SelectItem value="__none" disabled>
                  {isRTL ? "لا يوجد أطباء" : "No doctors available"}
                </SelectItem>
              ) : (
                uniqueDoctors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Requester info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={`block ${isRTL ? "text-right" : "text-left"}`}>
              {isRTL ? "اسم مقدّم الطلب" : "Requester Name"} *
            </Label>
            <Input
              value={formData.requester_name}
              onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })}
              placeholder={isRTL ? "اسمك" : "Your name"}
              className={isRTL ? "text-right" : "text-left"}
              required
              data-testid="mr-requester-name"
            />
          </div>
          <div className="space-y-2">
            <Label className={`block ${isRTL ? "text-right" : "text-left"}`}>
              {isRTL ? "رقم واتساب" : "WhatsApp"} *
            </Label>
            <div className="flex gap-2 items-center" dir="ltr">
              <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md px-3 h-10 text-sm font-medium text-slate-700 shrink-0">
                <Phone className="w-3.5 h-3.5 text-green-500" />
                <span>966+</span>
              </div>
              <Input
                value={formData.requester_whatsapp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  const cleaned = val.startsWith("0") ? val.slice(1) : val;
                  if (cleaned.length <= 9) setFormData({ ...formData, requester_whatsapp: cleaned });
                }}
                placeholder="5XXXXXXXX"
                className="text-left"
                dir="ltr"
                required
                data-testid="mr-requester-whatsapp"
              />
            </div>
          </div>
        </div>
        <p className={`text-xs text-slate-400 ${isRTL ? "text-right" : "text-left"}`}>
          {isRTL
            ? "سيتم إشعارك عبر واتساب فور جهوز التقرير."
            : "You will be notified via WhatsApp once the report is ready."}
        </p>

        <Button
          type="submit"
          className="w-full bg-sky-600 hover:bg-sky-700 py-6 text-lg text-white"
          disabled={submitting}
          data-testid="mr-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mx-2 text-white" />
              <span className="text-white">{isRTL ? "جاري الإرسال..." : "Submitting..."}</span>
            </>
          ) : (
            <>
              <FileText className="w-5 h-5 mx-2 text-white" />
              <span className="text-white">
                {isRTL ? "إرسال الطلب" : "Submit Request"}
              </span>
            </>
          )}
        </Button>
      </form>
    </CardContent>
  </Card>
  );
};
